import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, Download, ZoomIn, ZoomOut, List, X,
  Calendar, Globe, Map, Users, Rocket, GraduationCap, Briefcase, TrendingUp, Coins, Ticket, Handshake, Leaf,
  Building2, Sparkles, Compass, Search, FileBarChart2, RefreshCw, Heart,
  MapPin, Mail, Phone, Linkedin, Instagram, Globe2, Quote,
} from "lucide-react";
import { profilePages } from "@/data/profileContent";
import { IyafWordmark } from "@/components/brand/IyafMark";
import { org } from "@/data/content";

const ICONS = {
  calendar: Calendar, globe: Globe, map: Map, users: Users, rocket: Rocket, graduate: GraduationCap,
  briefcase: Briefcase, trending: TrendingUp, coins: Coins, ticket: Ticket, handshake: Handshake, leaf: Leaf,
  building: Building2, sparkles: Sparkles, compass: Compass, search: Search, report: FileBarChart2,
  refresh: RefreshCw, heart: Heart,
};

// Build a localised copy of a slide using i18n keys at profilePages.<id>.*
// preserves images, icons, colors, coords, numeric values.
function localiseSlide(t, d) {
  const id = d.id;
  const trA = (path, fb) => t(`profilePages.${id}.${path}`, { defaultValue: fb });
  const base = { ...d };

  if (id === "cover") {
    base.eyebrow = trA("eyebrow", d.eyebrow);
    base.title = trA("title", d.title);
    base.subtitle = trA("subtitle", d.subtitle);
    base.location = trA("location", d.location);
    base.badges = t(`profilePages.cover.badges`, { returnObjects: true, defaultValue: d.badges });
    return base;
  }
  if (id === "letter") {
    base.pageTitle = trA("pageTitle", d.pageTitle);
    base.eyebrow = trA("eyebrow", d.eyebrow);
    base.pullQuote = trA("pullQuote", d.pullQuote);
    base.body = [trA("body1", d.body[0]), trA("body2", d.body[1])];
    base.signature = trA("signature", d.signature);
    return base;
  }
  if (id === "at-a-glance") {
    base.pageTitle = trA("pageTitle", d.pageTitle);
    base.eyebrow = trA("eyebrow", d.eyebrow);
    base.title = trA("title", d.title);
    base.hero = { ...d.hero, k: trA("heroLabel", d.hero.k) };
    const labelMap = { 2016: "founded", 5: "countries", 13: "communities", 555: "assessed" };
    base.sub = d.sub.map((s) => ({ ...s, k: trA(`labels.${labelMap[s.v] || s.k}`, s.k) }));
    const tileMap = { "1,000": "trained", "3,000": "participants", "80%": "employed", "$2M+": "funding", "432": "events", "113": "mentors", "70%": "selfSust", "100%": "profit" };
    base.tiles = d.tiles.map((s) => ({ ...s, k: trA(`labels.${tileMap[s.v] || s.k}`, s.k) }));
    return base;
  }
  if (id === "where") {
    base.pageTitle = trA("pageTitle", d.pageTitle);
    base.eyebrow = trA("eyebrow", d.eyebrow);
    base.title = trA("title", d.title);
    base.body = trA("body", d.body);
    base.countries = d.countries.map((c) => ({ ...c, status: trA(`countryStatus.${c.status}`, c.status) }));
    const sm = { Communities: "communities", Countries: "countries", Forum: "forum" };
    base.stats = d.stats.map((s) => ({ ...s, k: trA(`stats.${sm[s.k] || s.k}`, s.k) }));
    return base;
  }
  if (id === "who-we-are") {
    base.pageTitle = trA("pageTitle", d.pageTitle);
    base.eyebrow = trA("eyebrow", d.eyebrow);
    base.title = trA("title", d.title);
    base.body = [trA("body1", d.body[0]), trA("body2", d.body[1])];
    base.leadership = d.leadership.map((p) => ({
      ...p,
      role: p.name.includes("Trad") ? trA("roles.trad", p.role) : trA("roles.ameer", p.role),
    }));
    const tm = { "Core team": "core", "Volunteers": "volunteers", "Mentors": "mentors", "Consulting trainers": "trainers" };
    base.teamStats = d.teamStats.map((s) => ({ ...s, k: trA(`teamLabels.${tm[s.k] || s.k}`, s.k) }));
    return base;
  }
  if (id === "the-model") {
    base.pageTitle = trA("pageTitle", d.pageTitle);
    base.eyebrow = trA("eyebrow", d.eyebrow);
    base.title = trA("title", d.title);
    base.body = trA("body", d.body);
    base.arms = d.arms.map((arm) => ({
      ...arm,
      name: trA(`arms.${arm.key}.name`, arm.name),
      desc: trA(`arms.${arm.key}.desc`, arm.desc),
      status: trA(`arms.${arm.key}.status`, arm.status),
    }));
    return base;
  }
  if (id === "hub") {
    base.pageTitle = trA("pageTitle", d.pageTitle);
    base.title = trA("title", d.title);
    base.paragraphs = [trA("p1", d.paragraphs[0]), trA("p2", d.paragraphs[1])];
    const labels = ["sqm", "initiatives", "events", "mentors", "legal", "training"];
    base.metrics = d.metrics.map((m, i) => ({ ...m, k: trA(`metricLabels.${labels[i]}`, m.k) }));
    base.outcomes = t(`profilePages.hub.outcomes`, { returnObjects: true, defaultValue: d.outcomes });
    return base;
  }
  if (id === "social-ent") {
    base.pageTitle = trA("pageTitle", d.pageTitle);
    base.title = trA("title", d.title);
    base.body = trA("body", d.body);
    const labels = ["assessed", "trained", "funding", "satisfaction"];
    base.metrics = d.metrics.map((m, i) => ({ ...m, k: trA(`metricLabels.${labels[i]}`, m.k) }));
    return base;
  }
  if (id === "social-emp") {
    base.pageTitle = trA("pageTitle", d.pageTitle);
    base.title = trA("title", d.title);
    base.paragraphs = [trA("p1", d.paragraphs[0]), trA("p2", d.paragraphs[1])];
    const labels = ["participants", "employed", "employers", "industries", "career", "satisfaction"];
    base.metrics = d.metrics.map((m, i) => ({ ...m, k: trA(`metricLabels.${labels[i]}`, m.k) }));
    base.outcomes = t(`profilePages.social-emp.outcomes`, { returnObjects: true, defaultValue: d.outcomes });
    return base;
  }
  if (id === "consult") {
    base.pageTitle = trA("pageTitle", d.pageTitle);
    base.title = trA("title", d.title);
    base.body = trA("body", d.body);
    const skeys = ["innovation", "evaluation", "research", "reporting"];
    base.services = d.services.map((s, i) => ({ ...s, name: trA(`services.${skeys[i]}`, s.name) }));
    const labels = ["engagements", "profit", "savings"];
    base.metrics = d.metrics.map((m, i) => ({ ...m, k: trA(`metricLabels.${labels[i]}`, m.k) }));
    return base;
  }
  if (id === "venture") {
    base.pageTitle = trA("pageTitle", d.pageTitle);
    base.title = trA("title", d.title);
    base.paragraphs = [trA("p1", d.paragraphs[0]), trA("p2", d.paragraphs[1])];
    const labels = ["launch", "cohort", "vcs", "accel"];
    base.metrics = d.metrics.map((m, i) => ({ ...m, k: trA(`metricLabels.${labels[i]}`, m.k) }));
    base.outcomes = t(`profilePages.venture.outcomes`, { returnObjects: true, defaultValue: d.outcomes });
    return base;
  }
  if (id === "voices") {
    base.pageTitle = trA("pageTitle", d.pageTitle);
    base.eyebrow = trA("eyebrow", d.eyebrow);
    base.title = trA("title", d.title);
    const keys = ["sarah", "ahmed", "lina", "yousef"];
    base.voices = d.voices.map((v, i) => ({ ...v, quote: trA(`items.${keys[i]}.quote`, v.quote), role: trA(`items.${keys[i]}.role`, v.role) }));
    return base;
  }
  if (id === "sustainability") {
    base.pageTitle = trA("pageTitle", d.pageTitle);
    base.eyebrow = trA("eyebrow", d.eyebrow);
    base.title = trA("title", d.title);
    base.body = trA("body", d.body);
    const cycleNames = t(`profilePages.sustainability.cycle`, { returnObjects: true, defaultValue: d.cycle.map((c) => c.name) });
    base.cycle = d.cycle.map((c, i) => ({ ...c, name: cycleNames[i] || c.name }));
    const pkeys = ["self", "reserve", "profit"];
    base.pillars = d.pillars.map((p, i) => ({ ...p, k: trA(`pillars.${pkeys[i]}`, p.k) }));
    return base;
  }
  if (id === "engage") {
    base.pageTitle = trA("pageTitle", d.pageTitle);
    base.eyebrow = trA("eyebrow", d.eyebrow);
    base.title = trA("title", d.title);
    const lkeys = ["strategic", "sponsor", "consulting", "join", "volunteer", "support"];
    base.lanes = d.lanes.map((l, i) => ({ ...l, name: trA(`lanes.${lkeys[i]}.name`, l.name), desc: trA(`lanes.${lkeys[i]}.desc`, l.desc) }));
    return base;
  }
  if (id === "contact") {
    base.pageTitle = trA("pageTitle", d.pageTitle);
    base.title = trA("title", d.title);
    base.body = trA("body", d.body);
    base.contacts = d.contacts.map((c) => ({ ...c, k: trA(`labels.${c.k}`, c.k) }));
    return base;
  }
  return base;
}

export default function ProfileViewer() {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [tocOpen, setTocOpen] = useState(true);
  const total = profilePages.length;

  const goNext = useCallback(() => setPage((p) => Math.min(p + 1, total - 1)), [total]);
  const goPrev = useCallback(() => setPage((p) => Math.max(p - 1, 0)), []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === "PageDown") goNext();
      if (e.key === "ArrowLeft" || e.key === "PageUp") goPrev();
      if (e.key === "Home") setPage(0);
      if (e.key === "End") setPage(total - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goNext, goPrev, total]);

  const current = localiseSlide(t, profilePages[page]);
  const handlePrint = () => window.print();

  return (
    <div className="relative min-h-screen bg-iyaf-paper" data-testid="profile-reader">
      <div className="sticky top-16 sm:top-20 z-30 bg-white/90 backdrop-blur-md border-b border-black/5 print:hidden">
        <div className="container-iyaf py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Link to="/about" data-testid="reader-back" className="text-sm font-semibold text-iyaf-ink/70 hover:text-iyaf-ink inline-flex items-center gap-1.5"><ArrowLeft size={15} /> {t("profile.back")}</Link>
            <span className="text-iyaf-ink/20">|</span>
            <button onClick={() => setTocOpen((v) => !v)} data-testid="toggle-toc" className="text-sm font-semibold text-iyaf-ink/70 hover:text-iyaf-ink inline-flex items-center gap-1.5">
              {tocOpen ? <X size={15} /> : <List size={15} />} {tocOpen ? t("profile.hideContents") : t("profile.showContents")}
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={goPrev} disabled={page === 0} data-testid="reader-prev" className="p-2 rounded-full border border-black/10 hover:bg-iyaf-paper disabled:opacity-40"><ChevronLeft size={15} /></button>
            <span className="text-xs font-mono text-iyaf-ink/65 min-w-[70px] text-center" data-testid="reader-page-indicator">{String(page + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}</span>
            <button onClick={goNext} disabled={page === total - 1} data-testid="reader-next" className="p-2 rounded-full border border-black/10 hover:bg-iyaf-paper disabled:opacity-40"><ChevronRight size={15} /></button>
            <span className="mx-2 text-iyaf-ink/20">|</span>
            <button onClick={() => setZoom((z) => Math.max(z - 0.1, 0.5))} data-testid="reader-zoom-out" className="p-2 rounded-full border border-black/10 hover:bg-iyaf-paper"><ZoomOut size={15} /></button>
            <span className="text-xs font-mono text-iyaf-ink/65 w-12 text-center">{Math.round(zoom * 100)}%</span>
            <button onClick={() => setZoom((z) => Math.min(z + 0.1, 1.4))} data-testid="reader-zoom-in" className="p-2 rounded-full border border-black/10 hover:bg-iyaf-paper"><ZoomIn size={15} /></button>
            <span className="mx-2 text-iyaf-ink/20">|</span>
            <button onClick={handlePrint} data-testid="reader-download" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-iyaf-ink text-white text-sm font-semibold hover:bg-iyaf-blue transition"><Download size={14} /> {t("profile.save")}</button>
          </div>
        </div>
      </div>

      <div className="container-iyaf py-8 grid lg:grid-cols-12 gap-6">
        <AnimatePresence>
          {tocOpen && (
            <motion.aside initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} className="lg:col-span-3 lg:sticky lg:top-36 self-start print:hidden" data-testid="reader-toc">
              <div className="bg-white rounded-2xl border border-black/5 p-5 max-h-[80vh] overflow-y-auto">
                <p className="eyebrow text-iyaf-blue mb-4">{t("profile.contents")}</p>
                <ol className="space-y-1">
                  {profilePages.map((p, i) => {
                    const loc = localiseSlide(t, p);
                    const locName = loc.pageTitle || loc.title || t("profileReader.coverShort", "Cover");
                    return (
                    <li key={p.id}>
                      <button onClick={() => setPage(i)} data-testid={`toc-${p.id}`} className={`group w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${i === page ? "text-white font-semibold" : "text-iyaf-ink/70 hover:bg-iyaf-paper"}`} style={i === page ? { background: p.accent } : {}}>
                        <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: i === page ? "#fff" : p.accent }} />
                        <span className="truncate">{(locName || "").replace(/^\d+\s·\s/, "")}</span>
                      </button>
                    </li>
                  );})}
                </ol>
              </div>
            </motion.aside>
          )}
        </AnimatePresence>

        <main className={`${tocOpen ? "lg:col-span-9" : "lg:col-span-12"} flex flex-col items-center`}>
          <div className="reader-stage relative w-full" style={{ maxWidth: `${zoom * 1100}px`, transition: "max-width 0.3s ease" }}>
            <AnimatePresence mode="wait">
              <motion.div key={page} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.25 }}>
                <Paper data={current} pageNumber={page + 1} total={total} t={t} />
              </motion.div>
            </AnimatePresence>
          </div>
          <div className="mt-8 mb-12 flex items-center gap-4 print:hidden">
            <button onClick={goPrev} disabled={page === 0} data-testid="reader-prev-bottom" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-black/10 hover:bg-white text-sm font-semibold disabled:opacity-40"><ArrowLeft size={14} /> {t("cta.previous")}</button>
            <button onClick={goNext} disabled={page === total - 1} data-testid="reader-next-bottom" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-iyaf-ink text-white text-sm font-semibold hover:bg-iyaf-blue disabled:opacity-40">{t("cta.next")} <ArrowRight size={14} /></button>
          </div>
        </main>
      </div>

      <div className="hidden print:block">
        {profilePages.map((p, i) => (
          <div key={p.id} className="print-page" style={{ pageBreakAfter: i === profilePages.length - 1 ? "auto" : "always" }}>
            <Paper data={localiseSlide(t, p)} pageNumber={i + 1} total={total} t={t} printOnly />
          </div>
        ))}
      </div>

      <style>{`
        @media print {
          body { background: white !important; }
          .reader-stage, .print\\:hidden { display: none !important; }
          .hidden.print\\:block { display: block !important; }
          .print-page { width: 297mm; height: 210mm; overflow: hidden; }
          .a4-land { box-shadow: none !important; border: none !important; border-radius: 0 !important; width: 297mm !important; height: 210mm !important; }
        }
        @page { size: A4 landscape; margin: 0; }
      `}</style>
    </div>
  );
}

/* --------------- Landscape paper wrapper --------------- */
function Paper({ data, pageNumber, total, t, printOnly = false }) {
  const T = TEMPLATES[data.type] || ArmSlide;
  const accent = data.accent || "#3663A8";
  const tt = t || ((s, f) => f || s);
  return (
    <div className="a4-land relative bg-white shadow-xl shadow-black/10 border border-black/5 mx-auto overflow-hidden" style={{ aspectRatio: "297 / 210" }} data-testid={printOnly ? undefined : `slide-${data.id}`}>
      {/* arm-colored corner sigils */}
      <div className="pointer-events-none absolute -right-32 -top-32 w-80 h-80 rounded-full" style={{ background: `${accent}0D` }} />
      <div className="pointer-events-none absolute -left-20 -bottom-20 w-52 h-52 rounded-full" style={{ background: `${accent}10` }} />
      <div className="pointer-events-none absolute top-0 left-0 right-0 h-[3px]" style={{ background: accent }} />

      <div className="relative w-full h-full px-[4.5%] py-[3.8%] flex flex-col">
        <T data={data} t={tt} />
        {data.type !== "cover" && (
          <div className="mt-auto pt-3 flex items-center justify-between text-[9px] text-iyaf-ink/45 border-t border-black/5">
            <span className="font-mono uppercase tracking-widest flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: accent }} />
              {tt("profileReader.footer", { org: org.name, defaultValue: `${org.name} · Profile · 2026` })}
            </span>
            <span className="font-mono">{String(pageNumber).padStart(2, "0")} / {String(total).padStart(2, "0")}</span>
          </div>
        )}
      </div>
    </div>
  );
}

/* --------------- Small primitives --------------- */
const Eyebrow = ({ children, color = "#3663A8" }) => (
  <p className="font-mono text-[9.5px] uppercase tracking-[0.28em] mb-2" style={{ color }}>{children}</p>
);
const PageMark = ({ children }) => (
  <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-iyaf-ink/40 mb-3">{children}</p>
);
const Title = ({ children, className = "" }) => (
  <h2 className={`font-display font-black tracking-tighter leading-[1.02] text-[28px] mb-3 ${className}`}>{children}</h2>
);
const TitleBig = ({ children, className = "" }) => (
  <h2 className={`font-display font-black tracking-tighter leading-[1.02] text-[34px] mb-3 ${className}`}>{children}</h2>
);
const Metric = ({ v, k, color = "#3663A8", size = "lg" }) => (
  <div className={`p-2.5 rounded-xl bg-iyaf-paper border border-black/5`}>
    <p className={`font-display font-black leading-none ${size === "xl" ? "text-3xl" : "text-lg"}`} style={{ color }}>{v}</p>
    <p className="text-[9px] uppercase tracking-widest text-iyaf-ink/55 mt-1.5">{k}</p>
  </div>
);

/* --------------- Page templates --------------- */

const Cover = ({ data, t }) => (
  <div className="h-full grid grid-cols-12 gap-6">
    <div className="col-span-7 flex flex-col">
      <IyafWordmark variant="main" size="xl" />
      <div className="my-auto">
        <Eyebrow color={data.accent}>{data.eyebrow}</Eyebrow>
        <h1 className="font-display font-black tracking-tighter leading-[0.92] text-[44px]">{data.title}</h1>
        <p className="mt-5 font-editorial italic text-[26px] text-iyaf-ink/80 leading-snug max-w-xl">{data.subtitle}</p>
        <div className="mt-6 flex items-center gap-2">
          {data.badges?.map((b, i) => (
            <span key={b} className="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest text-white" style={{ background: [data.accent, "#FF9F1C", "#8C8C8C", "#E01E84"][i] }}>{b}</span>
          ))}
        </div>
      </div>
      <div className="pt-4 border-t border-black/10 grid grid-cols-2 gap-4 text-xs">
        <div><p className="small-caps text-iyaf-ink/50">{data.location}</p><p className="mt-1 font-semibold">{t("footerExtras.address", org.hq)}</p></div>
        <div className="text-right"><p className="small-caps text-iyaf-ink/50">{t("profileReader.edition", "Edition")}</p><p className="mt-1 font-semibold">{t("profileReader.editionValue", "2026 · v1")}</p></div>
      </div>
    </div>
    <div className="col-span-5 relative rounded-[28px] overflow-hidden">
      <img src={data.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-iyaf-blue/60 via-iyaf-blue/10 to-transparent" />
      <div className="absolute bottom-5 left-5 right-5 text-white">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] opacity-80">{t("profileReader.anOrgProfile", "An organisational profile")}</p>
        <p className="font-editorial italic text-xl mt-1">{t("profileReader.coverStrap", "Building infrastructure for youth, across five countries.")}</p>
      </div>
    </div>
  </div>
);

const LetterSlide = ({ data, t }) => (
  <div className="flex-1 grid grid-cols-12 gap-6">
    <div className="col-span-7 flex flex-col">
      <PageMark>{data.pageTitle}</PageMark>
      <Eyebrow color={data.accent}>{data.eyebrow}</Eyebrow>
      <div className="relative pl-6 mb-4">
        <Quote size={26} className="absolute -left-1 -top-1 text-iyaf-blue/30" />
        <p className="font-editorial italic text-[26px] leading-[1.15] text-iyaf-ink">{data.pullQuote}</p>
      </div>
      <div className="space-y-3 text-iyaf-ink/80 leading-relaxed text-[12.5px]">
        {data.body.map((p, i) => <p key={i}>{p}</p>)}
      </div>
      <p className="mt-4 font-editorial italic text-iyaf-ink/65 text-base">{data.signature}</p>
    </div>
    <div className="col-span-5 flex flex-col gap-3">
      <div className="rounded-[20px] overflow-hidden flex-1 relative">
        <img src={data.image} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-iyaf-ink/60 to-transparent" />
      </div>
      <div className="grid grid-cols-6 gap-1.5">
        {data.portraits.map((src, i) => (
          <div key={i} className="aspect-square rounded-full overflow-hidden ring-2 ring-white shadow-md"><img src={src} alt="" className="w-full h-full object-cover" /></div>
        ))}
      </div>
    </div>
  </div>
);

const StatsSlideV2 = ({ data, t }) => {
  const HeroIcon = ICONS[data.hero.icon] || Sparkles;
  return (
    <div className="flex-1 flex flex-col">
      <div className="flex items-end justify-between mb-3">
        <div>
          <PageMark>{data.pageTitle}</PageMark>
          <Eyebrow color={data.accent}>{data.eyebrow}</Eyebrow>
          <Title>{data.title}</Title>
        </div>
        <IyafWordmark variant="main" size="sm" />
      </div>
      {/* Top row: hero + 4 sub */}
      <div className="grid gap-2.5 mb-2.5" style={{ gridTemplateColumns: "1.6fr 1fr 1fr 1fr 1fr" }}>
        <div className="row-span-1 rounded-2xl p-5 text-white relative overflow-hidden" style={{ background: data.accent }}>
          <div className="absolute -right-10 -top-10 w-44 h-44 rounded-full bg-white/10" />
          <div className="absolute -right-4 bottom-4 w-24 h-24 rounded-full bg-white/8" />
          <div className="relative flex items-start justify-between mb-2">
            <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center"><HeroIcon size={18} /></div>
            <span className="font-mono text-[9px] uppercase tracking-[0.25em] opacity-70">{t("profileReader.heroMetric", "Hero metric")}</span>
          </div>
          <div className="relative mt-6">
            <p className="font-display font-black text-[58px] leading-none tracking-tighter">{data.hero.v}</p>
            <p className="text-[11px] uppercase tracking-widest opacity-85 mt-2 max-w-[18ch]">{data.hero.k}</p>
          </div>
        </div>
        {data.sub.map((s) => {
          const Icon = ICONS[s.icon] || Sparkles;
          return (
            <div key={s.k} className="p-3 rounded-xl bg-iyaf-paper border border-black/5 flex flex-col">
              <div className="w-7 h-7 rounded-full bg-iyaf-blue/10 text-iyaf-blue flex items-center justify-center mb-2"><Icon size={13} /></div>
              <p className="font-display font-black text-2xl text-iyaf-ink leading-none">{s.v}</p>
              <p className="text-[9px] uppercase tracking-widest text-iyaf-ink/55 mt-auto pt-2">{s.k}</p>
            </div>
          );
        })}
      </div>
      {/* Bottom row: 8 tiles */}
      <div className="grid grid-cols-8 gap-2.5 flex-1">
        {data.tiles.map((t) => {
          const Icon = ICONS[t.icon] || Sparkles;
          return (
            <div key={t.k} className="p-2.5 rounded-xl bg-white border border-black/5 flex flex-col justify-between">
              <Icon size={12} className="text-iyaf-ink/40" />
              <div>
                <p className="font-display font-black text-base text-iyaf-ink leading-none">{t.v}</p>
                <p className="text-[8.5px] uppercase tracking-widest text-iyaf-ink/55 mt-1 leading-tight">{t.k}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const MapSlide = ({ data, t }) => (
  <div className="flex-1 grid grid-cols-12 gap-6">
    <div className="col-span-5 flex flex-col">
      <PageMark>{data.pageTitle}</PageMark>
      <Eyebrow color={data.accent}>{data.eyebrow}</Eyebrow>
      <Title>{data.title}</Title>
      <p className="font-editorial text-base text-iyaf-ink/80 leading-snug mb-4">{data.body}</p>
      <div className="grid grid-cols-3 gap-2 mb-4">
        {data.stats.map((s) => <Metric key={s.k} v={s.v} k={s.k} color={data.accent} />)}
      </div>
      <div className="space-y-1.5">
        {data.countries.map((c) => (
          <div key={c.name} className="flex items-center gap-3 px-3 py-2 rounded-xl bg-iyaf-paper border border-black/5">
            <span className="w-2 h-2 rounded-full" style={{ background: c.color }} />
            <span className="font-mono text-[10px] text-iyaf-ink/50">{c.flag}</span>
            <span className="font-semibold text-sm flex-1">{c.name}</span>
            <span className="text-[10px] text-iyaf-ink/55 uppercase tracking-widest">{c.status}</span>
            <span className="text-xs font-mono text-iyaf-ink/70">{c.communities} comm.</span>
          </div>
        ))}
      </div>
    </div>
    {/* Stylised world map (SVG dots over abstract continents) */}
    <div className="col-span-7 relative rounded-[24px] bg-gradient-to-br from-iyaf-blue/8 via-iyaf-paper to-iyaf-orange/8 border border-black/5 overflow-hidden">
      <svg viewBox="0 0 100 80" className="absolute inset-0 w-full h-full">
        {/* abstract landmass shapes */}
        <path d="M10,30 Q20,20 35,25 T55,28 Q65,30 70,40 L65,55 Q55,65 40,62 T15,55 Q5,45 10,30 Z" fill="#3663A8" fillOpacity="0.06" />
        <path d="M48,32 Q56,30 62,38 Q66,45 60,52 Q52,56 46,48 Q44,40 48,32 Z" fill="#3663A8" fillOpacity="0.08" />
        <path d="M55,55 Q63,55 65,65 Q63,72 55,72 Q47,70 50,62 Z" fill="#3663A8" fillOpacity="0.07" />
        <path d="M72,38 Q82,36 88,44 Q88,52 80,55 Q72,52 70,46 Z" fill="#3663A8" fillOpacity="0.06" />
        {/* grid */}
        {Array.from({ length: 9 }).map((_, i) => (
          <line key={`h${i}`} x1="0" y1={i * 10} x2="100" y2={i * 10} stroke="#3663A8" strokeOpacity="0.06" strokeWidth="0.2" />
        ))}
        {Array.from({ length: 11 }).map((_, i) => (
          <line key={`v${i}`} y1="0" x1={i * 10} y2="80" x2={i * 10} stroke="#3663A8" strokeOpacity="0.06" strokeWidth="0.2" />
        ))}
        {/* country pins */}
        {data.countries.map((c) => (
          <g key={c.name}>
            <circle cx={c.cx} cy={c.cy} r="4" fill={c.color} fillOpacity="0.18" />
            <circle cx={c.cx} cy={c.cy} r="2" fill={c.color} />
            <text x={c.cx + 3.5} y={c.cy + 1.2} fontSize="2.2" fill="#111827" fontWeight="700">{c.name}</text>
            <text x={c.cx + 3.5} y={c.cy + 3.6} fontSize="1.5" fill={c.color}>{c.status}</text>
          </g>
        ))}
        {/* HQ ring */}
        <circle cx="60" cy="44" r="6" fill="none" stroke="#3663A8" strokeOpacity="0.4" strokeWidth="0.3" strokeDasharray="0.6 0.6" />
      </svg>
      <div className="absolute bottom-3 right-4 font-mono text-[9px] uppercase tracking-widest text-iyaf-ink/45">{t("profileReader.fiveThirteen", "5 countries · 13 communities")}</div>
    </div>
  </div>
);

const FounderSlide = ({ data, t }) => (
  <div className="flex-1 grid grid-cols-12 gap-6">
    <div className="col-span-7">
      <PageMark>{data.pageTitle}</PageMark>
      <Eyebrow color={data.accent}>{data.eyebrow}</Eyebrow>
      <Title>{data.title}</Title>
      <div className="space-y-3 text-iyaf-ink/80 leading-relaxed text-[13px]">
        {data.body.map((p, i) => <p key={i} className={i === 0 ? "font-editorial text-lg text-iyaf-ink/90 leading-snug" : ""}>{p}</p>)}
      </div>
      <div className="mt-5 grid grid-cols-4 gap-2">
        {data.teamStats.map((s) => (
          <div key={s.k} className="p-3 rounded-xl bg-iyaf-blue/5 border border-iyaf-blue/10 text-center">
            <p className="font-display font-black text-2xl text-iyaf-blue leading-none">{s.v}</p>
            <p className="text-[9px] uppercase tracking-widest text-iyaf-ink/55 mt-1.5">{s.k}</p>
          </div>
        ))}
      </div>
    </div>
    <div className="col-span-5 flex flex-col gap-4 justify-center">
      {data.leadership.map((p) => (
        <div key={p.name} className="flex items-center gap-4 p-3 rounded-2xl bg-iyaf-paper border border-black/5">
          <div className="w-24 h-24 rounded-full overflow-hidden ring-4 ring-white shadow-lg shrink-0"><img src={p.image} alt={p.name} className="w-full h-full object-cover" /></div>
          <div>
            <p className="font-display font-black text-xl">{p.name}</p>
            <p className="text-sm text-iyaf-ink/60">{p.role}</p>
            <p className="font-mono text-[9px] uppercase tracking-widest text-iyaf-blue mt-1.5">{t("profileReader.founderCo", "Co-founder")}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

/* Orbital model diagram */
const ModelSlide = ({ data, t }) => (
  <div className="flex-1 grid grid-cols-12 gap-6">
    <div className="col-span-5 flex flex-col">
      <PageMark>{data.pageTitle}</PageMark>
      <Eyebrow color={data.accent}>{data.eyebrow}</Eyebrow>
      <Title>{data.title}</Title>
      <p className="text-iyaf-ink/75 text-[13px] leading-snug mb-4">{data.body}</p>
      <div className="space-y-2 mt-auto">
        {data.arms.map((arm) => {
          const Icon = ICONS[arm.icon] || Sparkles;
          return (
            <div key={arm.key} className="flex items-center gap-3 p-2.5 rounded-xl border" style={{ borderColor: `${arm.color}30`, background: `${arm.color}08` }}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: arm.color, color: "#fff" }}><Icon size={14} /></div>
              <div className="flex-1 min-w-0">
                <p className="font-display font-black text-sm" style={{ color: arm.color }}>{arm.name}</p>
                <p className="text-[10px] text-iyaf-ink/65 leading-tight">{arm.desc}</p>
              </div>
              <span className="font-mono text-[8.5px] uppercase tracking-widest" style={{ color: arm.color }}>{arm.status}</span>
            </div>
          );
        })}
      </div>
    </div>
    <div className="col-span-7 relative flex items-center justify-center">
      {/* Orbital SVG */}
      <svg viewBox="0 0 400 360" className="w-full h-full max-h-[420px]">
        <defs>
          <radialGradient id="bgGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#3663A8" stopOpacity="0.12" />
            <stop offset="100%" stopColor="#3663A8" stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle cx="200" cy="180" r="170" fill="url(#bgGlow)" />
        {/* orbit rings */}
        <circle cx="200" cy="180" r="130" fill="none" stroke="#3663A8" strokeOpacity="0.15" strokeWidth="1" strokeDasharray="3 4" />
        <circle cx="200" cy="180" r="90"  fill="none" stroke="#3663A8" strokeOpacity="0.12" strokeWidth="1" />
        {/* center IYAF */}
        <circle cx="200" cy="180" r="46" fill="#111827" />
        <text x="200" y="178" textAnchor="middle" fill="#fff" fontSize="14" fontWeight="900" fontFamily="Bricolage Grotesque, sans-serif">IYAF</text>
        <text x="200" y="194" textAnchor="middle" fill="#FF9F1C" fontSize="9" letterSpacing="2">FORUM</text>
        {/* 4 arm petals */}
        {[
          { ang: -90, color: "#3663A8", name: "Hub" },
          { ang: 0,   color: "#FF9F1C", name: "Social" },
          { ang: 90,  color: "#8C8C8C", name: "Consult" },
          { ang: 180, color: "#E01E84", name: "Venture" },
        ].map((p, i) => {
          const rad = (p.ang * Math.PI) / 180;
          const cx = 200 + Math.cos(rad) * 130;
          const cy = 180 + Math.sin(rad) * 130;
          return (
            <g key={i}>
              <line x1="200" y1="180" x2={cx} y2={cy} stroke={p.color} strokeOpacity="0.35" strokeWidth="1.2" strokeDasharray="2 3" />
              <circle cx={cx} cy={cy} r="34" fill={p.color} fillOpacity="0.12" />
              <circle cx={cx} cy={cy} r="26" fill={p.color} />
              <text x={cx} y={cy + 4} textAnchor="middle" fill="#fff" fontSize="11" fontWeight="800">{p.name}</text>
            </g>
          );
        })}
        {/* small satellites */}
        {Array.from({ length: 12 }).map((_, i) => {
          const ang = (i * 30 + 15) * Math.PI / 180;
          const r = 90;
          return <circle key={i} cx={200 + Math.cos(ang) * r} cy={180 + Math.sin(ang) * r} r="2" fill="#FF9F1C" fillOpacity="0.6" />;
        })}
      </svg>
      <p className="absolute bottom-0 right-2 font-mono text-[9px] uppercase tracking-widest text-iyaf-ink/45">{t("profileReader.oneFourArms", "One forum · Four arms")}</p>
    </div>
  </div>
);

const ArmSlide = ({ data, t }) => {
  const collage = data.collage || [];
  return (
    <div className="flex-1 grid grid-cols-12 gap-6">
      <div className="col-span-7 flex flex-col">
        <PageMark>{data.pageTitle}</PageMark>
        <IyafWordmark variant={data.armKey} size="lg" className="mb-3" />
        <TitleBig><span className="font-editorial italic font-normal" style={{ color: data.color }}>{data.title}</span></TitleBig>
        <div className="space-y-2.5 text-iyaf-ink/80 leading-relaxed text-[12.5px]">
          {(data.paragraphs || []).map((p, i) => <p key={i} className={i === 0 ? "font-editorial text-base text-iyaf-ink/90 leading-snug" : ""}>{p}</p>)}
        </div>
        {data.metrics && (
          <div className="mt-4 grid grid-cols-3 gap-2">
            {data.metrics.map((m) => (
              <div key={m.k} className="p-2.5 rounded-lg bg-iyaf-paper border border-black/5">
                <p className="font-display font-black text-lg leading-none" style={{ color: data.color }}>{m.v}</p>
                <p className="text-[9px] uppercase tracking-widest text-iyaf-ink/55 mt-1.5">{m.k}</p>
              </div>
            ))}
          </div>
        )}
        {data.outcomes && (
          <div className="mt-4 space-y-1.5">
            <p className="small-caps text-[10px] text-iyaf-ink/55">{t ? t("profileReader.outcomes", "Outcomes") : "Outcomes"}</p>
            {data.outcomes.map((o, i) => (
              <div key={i} className="flex items-start gap-2 text-[11.5px] text-iyaf-ink/85">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: data.color }} />
                <span>{o}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="col-span-5 flex flex-col gap-2.5">
        <div className="relative rounded-3xl overflow-hidden flex-1">
          <img src={data.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${data.color}55, transparent 60%)` }} />
          <div className="absolute bottom-3 left-3 right-3 text-white">
            <p className="font-mono text-[9px] uppercase tracking-[0.3em] opacity-80">{data.armKey?.toUpperCase()}</p>
          </div>
        </div>
        {collage.length > 0 && (
          <div className="grid grid-cols-2 gap-2">
            {collage.map((src, i) => (
              <div key={i} className="aspect-[4/3] rounded-xl overflow-hidden"><img src={src} alt="" className="w-full h-full object-cover" /></div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* 97/3 funnel slide */
const NinetyThreeSlide = ({ data, t }) => (
  <div className="flex-1 grid grid-cols-12 gap-6">
    <div className="col-span-6 flex flex-col">
      <PageMark>{data.pageTitle}</PageMark>
      <IyafWordmark variant="social" size="lg" className="mb-3" />
      <TitleBig><span className="font-editorial italic font-normal" style={{ color: data.color }}>{data.title}</span></TitleBig>
      <p className="font-editorial text-base text-iyaf-ink/85 leading-snug">{data.body}</p>
      <div className="mt-4 grid grid-cols-4 gap-2">
        {data.metrics.map((m) => (
          <div key={m.k} className="p-2.5 rounded-lg bg-iyaf-paper border border-black/5">
            <p className="font-display font-black text-lg leading-none text-iyaf-orange">{m.v}</p>
            <p className="text-[9px] uppercase tracking-widest text-iyaf-ink/55 mt-1.5">{m.k}</p>
          </div>
        ))}
      </div>
      <div className="mt-4">
        <p className="small-caps text-[10px] text-iyaf-ink/55 mb-2">{t("profileReader.ventures", "Ventures supported")}</p>
        <div className="flex flex-wrap gap-1.5">{data.ventures.map((v) => <span key={v} className="px-3 py-1 rounded-full bg-iyaf-orange/10 text-iyaf-orange text-xs font-semibold">{v}</span>)}</div>
      </div>
    </div>
    {/* SVG funnel */}
    <div className="col-span-6 flex flex-col">
      <div className="relative rounded-3xl bg-iyaf-ink p-6 flex-1 overflow-hidden">
        <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-iyaf-orange/15" />
        <p className="small-caps text-[10px] text-iyaf-orange mb-3 relative">{t("profileReader.funnelLabel", "The funnel")}</p>
        <svg viewBox="0 0 360 280" className="w-full h-auto relative">
          {/* funnel layers */}
          <defs>
            <linearGradient id="grayLay" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#8C8C8C" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#8C8C8C" stopOpacity="0.18" />
            </linearGradient>
          </defs>
          {/* Layer 1: 100 founders */}
          <path d="M20,30 L340,30 L300,90 L60,90 Z" fill="#ffffff" fillOpacity="0.1" stroke="#ffffff" strokeOpacity="0.3" />
          <text x="180" y="55" textAnchor="middle" fill="#fff" fontSize="14" fontWeight="800">{t("profileReader.funnel100", "100 founders assessed")}</text>
          <text x="180" y="78" textAnchor="middle" fill="#ffffff" fillOpacity="0.7" fontSize="11">{t("profileReader.funnelValidation", "Rigorous validation")}</text>
          {/* Layer 2: 97 redirected */}
          <path d="M60,100 L300,100 L260,180 L100,180 Z" fill="url(#grayLay)" stroke="#8C8C8C" strokeOpacity="0.5" />
          <text x="180" y="130" textAnchor="middle" fill="#8C8C8C" fontSize="40" fontWeight="900">97</text>
          <text x="180" y="155" textAnchor="middle" fill="#ffffff" fillOpacity="0.85" fontSize="11" fontWeight="700">{t("profileReader.funnelRedirection", "Honest redirection")}</text>
          <text x="180" y="170" textAnchor="middle" fill="#ffffff" fillOpacity="0.55" fontSize="9">{t("profileReader.funnelRedirectionSub", "into employment, skills, partnerships")}</text>
          {/* Layer 3: 3 accelerated */}
          <path d="M100,190 L260,190 L220,260 L140,260 Z" fill="#FF9F1C" />
          <text x="180" y="226" textAnchor="middle" fill="#111827" fontSize="38" fontWeight="900">3</text>
          <text x="180" y="248" textAnchor="middle" fill="#111827" fontSize="11" fontWeight="700">{t("profileReader.funnelIntensive", "Intensive support")}</text>
          {/* annotations side */}
          <line x1="305" y1="60" x2="345" y2="60" stroke="#fff" strokeOpacity="0.4" strokeDasharray="2 2" />
          <text x="350" y="63" fill="#ffffff" fillOpacity="0.85" fontSize="9">{t("profileReader.funnelAnn1", "555 assessed total")}</text>
          <line x1="265" y1="140" x2="345" y2="140" stroke="#8C8C8C" strokeDasharray="2 2" />
          <text x="350" y="143" fill="#8C8C8C" fontSize="9">{t("profileReader.funnelAnn2", "→ employment track")}</text>
          <line x1="225" y1="220" x2="345" y2="220" stroke="#FF9F1C" strokeDasharray="2 2" />
          <text x="350" y="223" fill="#FF9F1C" fontSize="9">{t("profileReader.funnelAnn3", "→ Venture (2027)")}</text>
        </svg>
      </div>
    </div>
  </div>
);

const ConsultSlide = ({ data, t }) => (
  <div className="flex-1 grid grid-cols-12 gap-6">
    <div className="col-span-7">
      <PageMark>{data.pageTitle}</PageMark>
      <IyafWordmark variant="consult" size="lg" className="mb-3" />
      <TitleBig><span className="font-editorial italic font-normal text-iyaf-ink/85">{data.title}</span></TitleBig>
      <p className="font-editorial text-base text-iyaf-ink/85 leading-snug">{data.body}</p>
      <div className="mt-4 grid grid-cols-3 gap-2">
        {data.metrics.map((m) => (
          <div key={m.k} className="p-2.5 rounded-lg bg-iyaf-paper border border-black/5">
            <p className="font-display font-black text-lg leading-none text-iyaf-ink">{m.v}</p>
            <p className="text-[9px] uppercase tracking-widest text-iyaf-ink/55 mt-1.5">{m.k}</p>
          </div>
        ))}
      </div>
      <div className="mt-4">
        <p className="small-caps text-[10px] text-iyaf-ink/55 mb-2">{t("profileReader.clients", "Clients")}</p>
        <div className="flex flex-wrap gap-1.5">{data.clients.map((c) => <span key={c} className="px-3 py-1 rounded-full bg-iyaf-ink/5 text-iyaf-ink text-[11px] font-semibold">{c}</span>)}</div>
      </div>
    </div>
    <div className="col-span-5 flex flex-col gap-2">
      <p className="small-caps text-[10px] text-iyaf-ink/55">{t("profileReader.services", "Service lines")}</p>
      {data.services.map((s, i) => {
        const Icon = ICONS[s.icon] || Sparkles;
        return (
          <div key={s.name} className="flex items-center gap-3 p-3 rounded-xl bg-iyaf-paper border border-black/5">
            <span className="font-mono text-[9px] text-iyaf-ink/40 w-5">{String(i + 1).padStart(2, "0")}</span>
            <div className="w-9 h-9 rounded-full bg-iyaf-gray/15 text-iyaf-gray flex items-center justify-center shrink-0"><Icon size={16} /></div>
            <p className="font-semibold text-sm">{s.name}</p>
          </div>
        );
      })}
    </div>
  </div>
);

const VoicesSlide = ({ data, t }) => (
  <div className="flex-1 flex flex-col">
    <div className="flex items-end justify-between mb-4">
      <div>
        <PageMark>{data.pageTitle}</PageMark>
        <Eyebrow color={data.accent}>{data.eyebrow}</Eyebrow>
        <Title>{data.title}</Title>
      </div>
      <Quote size={42} className="text-iyaf-orange/40" />
    </div>
    <div className="grid grid-cols-2 gap-3 flex-1">
      {data.voices.map((v, i) => (
        <div key={i} className="relative rounded-2xl bg-white border border-black/5 p-4 flex flex-col">
          <div className="absolute -left-1 top-0 bottom-0 w-1 rounded-l-2xl" style={{ background: v.color }} />
          <Quote size={18} className="opacity-30" style={{ color: v.color }} />
          <p className="mt-2 font-editorial italic text-[15px] text-iyaf-ink leading-snug flex-1">&ldquo;{v.quote}&rdquo;</p>
          <div className="mt-3 pt-3 border-t border-black/5 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full overflow-hidden ring-2 ring-white shadow shrink-0"><img src={v.image} alt={v.name} className="w-full h-full object-cover" /></div>
            <div className="flex-1 min-w-0">
              <p className="font-display font-black text-sm leading-tight">{v.name}</p>
              <p className="text-[10px] text-iyaf-ink/60">{v.role}</p>
            </div>
            <span className="font-mono text-[9px] uppercase tracking-widest" style={{ color: v.color }}>{t("profileReader.voicesPrefix", "V")}{String(i + 1).padStart(2, "0")}</span>
          </div>
        </div>
      ))}
    </div>
  </div>
);

/* Circular cycle diagram */
const CycleSlide = ({ data, t }) => {
  const n = data.cycle.length;
  const R = 100;
  const cx = 260, cy = 180;
  return (
    <div className="flex-1 grid grid-cols-12 gap-6">
      <div className="col-span-5 flex flex-col">
        <PageMark>{data.pageTitle}</PageMark>
        <Eyebrow color={data.accent}>{data.eyebrow}</Eyebrow>
        <Title>{data.title}</Title>
        <p className="font-editorial text-base text-iyaf-ink/85 leading-snug">{data.body}</p>
        <div className="mt-4 space-y-2">
          {data.pillars.map((p) => (
            <div key={p.k} className="flex items-center gap-3 p-3 rounded-xl border" style={{ borderColor: `${p.color}30`, background: `${p.color}08` }}>
              <p className="font-display font-black text-2xl leading-none" style={{ color: p.color }}>{p.v}</p>
              <p className="text-[11px] uppercase tracking-widest text-iyaf-ink/65">{p.k}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="col-span-7 flex items-center justify-center relative">
        <svg viewBox="0 0 520 360" className="w-full h-full max-h-[420px]">
          {/* dashed orbit */}
          <circle cx={cx} cy={cy} r={R} fill="none" stroke="#3663A8" strokeOpacity="0.2" strokeWidth="1.2" strokeDasharray="4 5" />
          {/* center */}
          <circle cx={cx} cy={cy} r="44" fill="#111827" />
          <text x={cx} y={cy - 4} textAnchor="middle" fill="#FF9F1C" fontSize="6.5" letterSpacing="1.6">{t("profileReader.sustainabilityEyebrow", "SUSTAINABILITY")}</text>
          <text x={cx} y={cy + 14} textAnchor="middle" fill="#fff" fontSize="16" fontWeight="900" fontStyle="italic" fontFamily="Instrument Serif, serif">{t("profileReader.cycle", "cycle")}</text>
          {data.cycle.map((step, i) => {
            const ang = (i / n) * 2 * Math.PI - Math.PI / 2;
            const x = cx + Math.cos(ang) * R;
            const y = cy + Math.sin(ang) * R;
            const Icon = ICONS[step.icon] || Sparkles;
            // text radial offset — push out enough to clear the 22px node
            const lr = R + 58;
            const lx = cx + Math.cos(ang) * lr;
            const ly = cy + Math.sin(ang) * lr;
            // text anchor based on horizontal position
            const anchor = Math.abs(Math.cos(ang)) < 0.3 ? "middle" : (Math.cos(ang) > 0 ? "start" : "end");
            return (
              <g key={i}>
                {/* connector */}
                <line x1={x + Math.cos(ang) * 22} y1={y + Math.sin(ang) * 22} x2={cx + Math.cos(ang) * (R + 36)} y2={cy + Math.sin(ang) * (R + 36)} stroke="#3663A8" strokeOpacity="0.25" strokeWidth="0.8" strokeDasharray="2 2" />
                <circle cx={x} cy={y} r="22" fill="#fff" stroke="#3663A8" strokeWidth="1.4" />
                <foreignObject x={x - 9} y={y - 9} width="18" height="18">
                  <div style={{ color: "#3663A8" }}><Icon size={18} /></div>
                </foreignObject>
                <text x={lx} y={ly} textAnchor={anchor} fill="#111827" fontSize="10" fontWeight="700">{step.name}</text>
                <text x={lx} y={ly + 12} textAnchor={anchor} fill="#3663A8" fontSize="7" letterSpacing="1.5">{t("profileReader.step", "STEP")} {String(i + 1).padStart(2, "0")}</text>
              </g>
            );
          })}
          {/* small dots between steps */}
          {Array.from({ length: n }).map((_, i) => {
            const ang = ((i + 0.5) / n) * 2 * Math.PI - Math.PI / 2;
            const x = cx + Math.cos(ang) * R;
            const y = cy + Math.sin(ang) * R;
            return <circle key={i} cx={x} cy={y} r="2" fill="#FF9F1C" />;
          })}
        </svg>
      </div>
    </div>
  );
};

const EngageSlide = ({ data, t }) => (
  <div className="flex-1 flex flex-col">
    <PageMark>{data.pageTitle}</PageMark>
    <Eyebrow color={data.accent}>{data.eyebrow}</Eyebrow>
    <TitleBig>{data.title}</TitleBig>
    <div className="grid grid-cols-3 gap-3 flex-1 mt-2">
      {data.lanes.map((l, i) => {
        const Icon = ICONS[l.icon] || Sparkles;
        return (
          <div key={l.name} className="relative p-4 rounded-2xl border-2 overflow-hidden flex flex-col" style={{ borderColor: `${l.color}33`, background: `${l.color}08` }}>
            <div className="absolute -right-8 -top-8 w-24 h-24 rounded-full" style={{ background: `${l.color}18` }} />
            <div className="relative">
              <div className="w-10 h-10 rounded-full flex items-center justify-center mb-2" style={{ background: l.color, color: "#fff" }}><Icon size={16} /></div>
              <p className="font-mono text-[9px] text-iyaf-ink/40">{t("profileReader.lane", "LANE")} {String(i + 1).padStart(2, "0")}</p>
              <p className="font-display font-black text-base mt-1 leading-tight" style={{ color: l.color }}>{l.name}</p>
              {l.desc && <p className="text-[11px] text-iyaf-ink/70 mt-1.5 leading-snug">{l.desc}</p>}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

const ContactSlide = ({ data, t }) => (
  <div className="flex-1 grid grid-cols-12 gap-6">
    <div className="col-span-7 flex flex-col">
      <PageMark>{data.pageTitle}</PageMark>
      <IyafWordmark variant="main" size="md" className="mb-5" />
      <TitleBig><span className="font-editorial italic font-normal text-iyaf-blue">{data.title}</span></TitleBig>
      <p className="font-editorial text-lg text-iyaf-ink/85 leading-snug">{data.body}</p>
      <div className="mt-auto p-5 rounded-2xl bg-iyaf-ink text-white">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-iyaf-orange">{t("profileReader.thanks", "Thank you for reading.")}</p>
        <p className="font-editorial italic text-2xl mt-2">{t("profileReader.letsBuild", "Let's build the next chapter together.")}</p>
      </div>
    </div>
    <div className="col-span-5 grid grid-cols-1 gap-2 content-start">
      {data.contacts.map((c) => {
        const Icon = { Email: Mail, Phone: Phone, Address: MapPin, Web: Globe2, Instagram: Instagram, LinkedIn: Linkedin }[c.k] || Globe2;
        return (
          <div key={c.k} className="flex items-center gap-3 p-3 rounded-xl bg-iyaf-paper border border-black/5">
            <div className="w-9 h-9 rounded-full bg-iyaf-blue/10 text-iyaf-blue flex items-center justify-center shrink-0"><Icon size={15} /></div>
            <div className="flex-1 min-w-0">
              <p className="text-[9px] uppercase tracking-widest text-iyaf-ink/50">{c.k}</p>
              <p className="text-xs font-semibold truncate">{c.v}</p>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

const TEMPLATES = {
  cover: Cover,
  letterSlide: LetterSlide,
  statsSlide: StatsSlideV2,
  mapSlide: MapSlide,
  founderSlide: FounderSlide,
  modelSlide: ModelSlide,
  armSlide: ArmSlide,
  ninetyThreeSlide: NinetyThreeSlide,
  consultSlide: ConsultSlide,
  voicesSlide: VoicesSlide,
  cycleSlide: CycleSlide,
  engageSlide: EngageSlide,
  contactSlide: ContactSlide,
};
